import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import { 
    Search, RefreshCw, FlaskConical, Building, Mail, Phone, MapPin, Eye, Lock, Unlock, Trash2 
} from 'lucide-react';

// --- ФУНКЦИИ ФОРМАТИРОВАНИЯ ---
const formatPhone = (value) => {
    if (!value) return '+992 ';
    const numbers = value.replace(/\D/g, '');
    const tjNum = numbers.startsWith('992') ? numbers.slice(3) : numbers;
    let res = '+992 ';
    if (tjNum.length > 0) res += tjNum.substring(0, 3);
    if (tjNum.length > 3) res += '-' + tjNum.substring(3, 5);
    if (tjNum.length > 5) res += '-' + tjNum.substring(5, 7);
    if (tjNum.length > 7) res += '-' + tjNum.substring(7, 9);
    return res;
};

// Функция для разбора составного адреса (Регион|||Город|||Улица)
const parseAddress = (addressStr) => {
    let region = '', city = '', street = '';
    if (addressStr && addressStr.includes('|||')) {
        const parts = addressStr.split('|||');
        region = parts[0] || '';
        city = parts[1] || '';
        street = parts[2] || '';
    } else {
        street = addressStr || '';
    }
    return { region, city, street };
};

const Labs = () => {
    const [labs, setLabs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    // Фильтры и Пагинация
    const [searchQuery, setSearchQuery] = useState('');
    const [visibleCount, setVisibleCount] = useState(10);

    // Модалка "Подробнее"
    const [selectedLab, setSelectedLab] = useState(null);

    // Сброс пагинации при поиске
    useEffect(() => {
        setVisibleCount(10);
    }, [searchQuery]);

    // Бесконечный скролл (Lazy Load)
    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 200) {
                setVisibleCount(prev => prev + 10);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            // Сервер сам подтянет данные больницы (Preload("Hospital"))
            const res = await api.get('/users?role=lab');
            setLabs(res.data);
        } catch (error) {
            console.error(error);
            alert("Ошибка загрузки данных лабораторий");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Фильтрация
    const filteredLabs = labs.filter(lab => {
        const query = searchQuery.toLowerCase();
        return (
            (lab.organization_name || '').toLowerCase().includes(query) ||
            (lab.full_name || '').toLowerCase().includes(query) ||
            (lab.email || '').toLowerCase().includes(query) ||
            (lab.phone || '').toLowerCase().includes(query) ||
            (lab.address || '').toLowerCase().includes(query)
        );
    });

    // Отрезаем для Lazy Load
    const visibleLabs = filteredLabs.slice(0, visibleCount);

    // Действия
    const handleDelete = async (id) => {
        if (!window.confirm("Удалить эту лабораторию?")) return;
        try {
            await api.delete(`/users/${id}`);
            setLabs(labs.filter(l => l.id !== id));
        } catch (e) { alert("Ошибка удаления"); }
    };

    const handleToggleStatus = async (lab) => {
        const newStatus = !lab.is_active;
        try {
            await api.put(`/users/${lab.id}`, { is_active: newStatus });
            setLabs(labs.map(l => l.id === lab.id ? { ...l, is_active: newStatus } : l));
        } catch (e) { alert("Ошибка изменения статуса"); }
    };

    // --- POPUP: ПОДРОБНАЯ ИНФОРМАЦИЯ ---
    const LabDetailsModal = ({ lab, onClose }) => {
        if (!lab) return null;
        
        const { region, city, street } = parseAddress(lab.address);
        const fullAddress = [region, city, street].filter(Boolean).join(', ');

        return (
            <div className="modal-overlay" onClick={onClose} style={overlayStyle}>
                <div className="modal-content" onClick={e => e.stopPropagation()} style={modalStyle}>
                    <div style={{ marginBottom: '24px' }}>
                        <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#111827', marginTop: '0', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FlaskConical size={24} color="#3b82f6"/> Подробная информация
                        </h3>
                        <p style={{ color: '#6b7280', margin: 0 }}>{lab.organization_name || lab.full_name}</p>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px', background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                        <div><p style={detailLabel}>Владелец / ФИО:</p><p style={detailValue}>{lab.full_name || 'Не указано'}</p></div>
                        <div><p style={detailLabel}>Тип лаборатории:</p><p style={detailValue}>{lab.lab_type || 'Клиническая'}</p></div>
                        <div><p style={detailLabel}>Номер лицензии:</p><p style={detailValue}>{lab.license_number || 'Не указан'}</p></div>
                        <div><p style={detailLabel}>Прикреплена к больнице:</p><p style={detailValue}>{lab.hospital?.name || 'Независимая'}</p></div>
                        <div><p style={detailLabel}>Email:</p><p style={detailValue}>{lab.email}</p></div>
                        <div><p style={detailLabel}>Телефон:</p><p style={detailValue}>{formatPhone(lab.phone)}</p></div>
                        <div style={{gridColumn: '1 / -1'}}><p style={detailLabel}>Полный адрес:</p><p style={detailValue}>{fullAddress || 'Не указан'}</p></div>
                    </div>

                    <button onClick={onClose} style={closeBtnStyle}>Закрыть</button>
                </div>
            </div>
        );
    };

    return (
        <div className="content-wrapper">
            <style>{`
                .animate-spin { animation: spin 1s linear infinite; }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                
                /* Стили карточки как на скриншоте */
                .lab-card {
                    background: white; border: 1px solid #e5e7eb; border-radius: 12px; padding: 20px;
                    display: flex; gap: 20px; margin-bottom: 16px;
                }
                .lab-icon-box {
                    width: 50px; height: 50px; border-radius: 50%; background: #eff6ff; color: #3b82f6;
                    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
                }
                .lab-title-row { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; }
                .lab-title { font-size: 1.15rem; font-weight: 600; color: #111827; margin: 0; }
                
                /* Бейджики */
                .badge-active { background: #dcfce7; color: #166534; padding: 2px 10px; border-radius: 12px; font-size: 0.75rem; font-weight: 500; border: 1px solid #bbf7d0; }
                .badge-blocked { background: #fee2e2; color: #991b1b; padding: 2px 10px; border-radius: 12px; font-size: 0.75rem; font-weight: 500; border: 1px solid #fecaca; }
                .badge-hospital { display: inline-flex; align-items: center; gap: 6px; background: #eff6ff; color: #2563eb; padding: 4px 12px; border-radius: 20px; font-size: 0.8rem; font-weight: 500; border: 1px solid #bfdbfe; margin-bottom: 16px; }
                
                /* Сетка инфо */
                .lab-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 16px; }
                .lab-info-item { display: flex; align-items: center; gap: 10px; font-size: 0.9rem; color: #4b5563; }
                
                /* Кнопки справа */
                .lab-actions { display: flex; flex-direction: column; gap: 10px; min-width: 180px; justify-content: flex-start; }
                .btn-outline-action { 
                    display: flex; align-items: center; justify-content: center; gap: 8px; 
                    padding: 8px 16px; border-radius: 8px; font-size: 0.85rem; font-weight: 500; 
                    cursor: pointer; transition: all 0.2s; background: white; width: 100%;
                }
                .btn-blue { border: 1px solid #93c5fd; color: #2563eb; }
                .btn-blue:hover { background: #eff6ff; }
                .btn-orange { border: 1px solid #fdba74; color: #ea580c; }
                .btn-orange:hover { background: #fff7ed; }
                .btn-green { border: 1px solid #86efac; color: #16a34a; }
                .btn-green:hover { background: #f0fdf4; }
                .btn-red { border: 1px solid #fca5a5; color: #dc2626; }
                .btn-red:hover { background: #fef2f2; }
            `}</style>
            
            {/* ШАПКА */}
            <div className="card doc-controls-card" style={{margin: '0'}}>
                <div className="card-header doc-header-flex">
                    <div className="doc-title-block">
                        <h4 className="card-title" style={{display:'flex', alignItems:'center', gap:'10px'}}>
                            <FlaskConical size={24} /> Управление лабораториями
                        </h4>
                        <p className="card-subtitle">Всего: {labs.length} • Активных: {labs.filter(l => l.is_active).length}</p>
                    </div>
                    <button className="flex-none btn-outline btn-sm" onClick={fetchData} style={{display:'flex', gap:'8px', alignItems:'center'}}>
                        <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} /> Обновить
                    </button>
                </div>
                
                <div className="card-body doc-filter-body">
                    <div className="search-wrapper" style={{width: '100%'}}>
                        <Search className="search-icon" size={18} />
                        <input 
                            type="text" className="search-input" 
                            placeholder="Поиск по названию, владельцу, email..." 
                            value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
            </div>
            
            <div>
                {visibleLabs.length === 0 && !isLoading && (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>Ничего не найдено</div>
                )}
                
                {visibleLabs.map(lab => {
                    const { city, street } = parseAddress(lab.address);
                    const title = lab.organization_name || lab.full_name;

                    return (
                        <div key={lab.id} className="lab-card" style={{ opacity: lab.is_active ? 1 : 0.8 }}>
                            {/* Иконка */}
                            <div className="lab-icon-box">
                                <FlaskConical size={28}/>
                            </div>
                            
                            {/* Основная информация (по центру) */}
                            <div style={{ flex: 1 }}>
                                <div className="lab-title-row">
                                    <h3 className="lab-title">{title}</h3>
                                    <span className={lab.is_active ? "badge-active" : "badge-blocked"}>
                                        {lab.is_active ? 'Активна' : 'Заблокирована'}
                                    </span>
                                </div>
                                
                                {/* Бейджик больницы (если есть) */}
                                {lab.hospital && (
                                    <div className="badge-hospital">
                                        <Building size={14}/> {lab.hospital.name}
                                    </div>
                                )}

                                {/* Сетка контактов */}
                                <div className="lab-info-grid">
                                    <div className="lab-info-item"><Mail size={16} color="#9ca3af"/> {lab.email}</div>
                                    <div className="lab-info-item"><Phone size={16} color="#9ca3af"/> {formatPhone(lab.phone)}</div>
                                    
                                    {/* Показываем адрес и город, если они есть */}
                                    {street && <div className="lab-info-item"><MapPin size={16} color="#9ca3af"/> {street}</div>}
                                    {city && <div className="lab-info-item"><Building size={16} color="#9ca3af"/> {city}</div>}
                                </div>

                                <p style={{ fontSize: '0.8rem', color: '#9ca3af', margin: 0 }}>
                                    Зарегистрирована: {new Date(lab.created_at).toLocaleDateString()}
                                </p>
                            </div>
                            
                            {/* Кнопки действий (Справа) */}
                            <div className="lab-actions">
                                <button className="btn-outline-action btn-blue" onClick={() => setSelectedLab(lab)}>
                                    <Eye size={16}/> Подробнее
                                </button>
                                
                                <button 
                                    className={`btn-outline-action ${lab.is_active ? 'btn-orange' : 'btn-green'}`} 
                                    onClick={() => handleToggleStatus(lab)}
                                >
                                    {lab.is_active ? <><Lock size={16}/> Заблокировать</> : <><Unlock size={16}/> Разблокировать</>}
                                </button>
                                
                                <button className="btn-outline-action btn-red" onClick={() => handleDelete(lab.id)}>
                                    <Trash2 size={16}/> Удалить
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
            
            {/* ИНДИКАТОР ПРОКРУТКИ */}
            {!isLoading && visibleLabs.length < filteredLabs.length && (
                <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                    Прокрутите вниз, чтобы загрузить еще...
                </div>
            )}

            <LabDetailsModal lab={selectedLab} onClose={() => setSelectedLab(null)} />
        </div>
    );
};

// --- СТИЛИ ДЛЯ МОДАЛКИ ---
const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(2px)' };
const modalStyle = { backgroundColor: 'white', borderRadius: '16px', width: '600px', maxWidth: '95%', padding: '32px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', position: 'relative' };
const detailLabel = { fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px', margin: 0 };
const detailValue = { fontSize: '1rem', fontWeight: '600', color: '#111827', margin: 0 };
const closeBtnStyle = { width: '100%', padding: '12px', backgroundColor: '#0f172a', color: 'white', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '500', cursor: 'pointer' };

export default Labs;